import { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, X } from 'lucide-react';

// --- Color conversion helpers ---

function hsbToHex(h, s, b) {
  // h: 0-360, s: 0-1, b: 0-1
  const c = b * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = b - c;
  let r, g, bl;
  if (h < 60) { r = c; g = x; bl = 0; }
  else if (h < 120) { r = x; g = c; bl = 0; }
  else if (h < 180) { r = 0; g = c; bl = x; }
  else if (h < 240) { r = 0; g = x; bl = c; }
  else if (h < 300) { r = x; g = 0; bl = c; }
  else { r = c; g = 0; bl = x; }
  const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(bl)}`;
}

function hexToHsb(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + 6) % 6 * 60;
    else if (max === g) h = ((b - r) / d + 2) * 60;
    else h = ((r - g) / d + 4) * 60;
  }
  return { h, s: max === 0 ? 0 : d / max, b: max };
}

function hueToRgbHex(h) {
  return hsbToHex(h, 1, 1);
}

// --- Preset grid colors ---

const PRESET_COLORS = [
  '#000000', '#FFFFFF', '#9CA3AF', '#EF4444',
  '#F97316', '#EAB308', '#3B82F6', '#A855F7',
  '#22C55E',
];

// --- Inline SVGs ---

function TransparentIcon({ size = 36, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="15" fill="none" stroke={active ? '#4F46E5' : '#D1D5DB'} strokeWidth="2" />
      <line x1="27" y1="9" x2="9" y2="27" stroke={active ? '#4F46E5' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function TransparentSwatchSmall() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" className="block">
      <circle cx="12" cy="12" r="10" fill="none" stroke="#D1D5DB" strokeWidth="2" />
      <line x1="18" y1="6" x2="6" y2="18" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * ColorPicker — A Smallpdf-style color picker with preset grid + custom HSB picker.
 *
 * Props:
 *   value        — current hex color (e.g. "#000000") or "transparent"
 *   onChange(hex) — callback when color changes (hex string or "transparent")
 *   label        — tooltip text
 *   allowTransparent — show "None" option (default false)
 */
export default function ColorPicker({ value = '#000000', onChange, label = 'Color', allowTransparent = false }) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const [flipUp, setFlipUp] = useState(false);
  const [popoverLeft, setPopoverLeft] = useState(null);

  // Custom picker state
  const initialHsb = value && value !== 'transparent' ? hexToHsb(value) : { h: 0, s: 1, b: 1 };
  const [customHue, setCustomHue] = useState(initialHsb.h);
  const [customSat, setCustomSat] = useState(initialHsb.s);
  const [customBri, setCustomBri] = useState(initialHsb.b);
  const [customHex, setCustomHex] = useState(value !== 'transparent' ? value : '#FF0000');

  // Canvas refs
  const sbCanvasRef = useRef(null);
  const hueCanvasRef = useRef(null);
  const sbDragging = useRef(false);
  const hueDragging = useRef(false);

  // Sync custom hex when HSB changes
  useEffect(() => {
    setCustomHex(hsbToHex(customHue, customSat, customBri));
  }, [customHue, customSat, customBri]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setShowCustom(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setShowCustom(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Check if popover should flip above button
  useEffect(() => {
    if (!open || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setFlipUp(spaceBelow < 320);
  }, [open, showCustom]);

  // Calculate horizontal position so popover stays within viewport
  useEffect(() => {
    if (!open || !popoverRef.current || !containerRef.current) return;
    const popRect = popoverRef.current.getBoundingClientRect();
    const btnRect = containerRef.current.getBoundingClientRect();

    // Default: align left edge of popover with left edge of button
    let left = 0;

    // If popover extends past right edge of viewport, shift it left
    if (btnRect.left + popRect.width > window.innerWidth - 8) {
      left = -(popRect.width - btnRect.width);
    }

    // If popover would go off the left edge, clamp it
    if (btnRect.left + left < 8) {
      left = -btnRect.left + 8;
    }

    setPopoverLeft(left);
  }, [open, showCustom]);

  // Draw saturation/brightness canvas
  const drawSBCanvas = useCallback(() => {
    const canvas = sbCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    // Base color for the current hue
    const hueColor = hueToRgbHex(customHue);

    // Horizontal gradient: white → hue color
    const gradH = ctx.createLinearGradient(0, 0, w, 0);
    gradH.addColorStop(0, '#FFFFFF');
    gradH.addColorStop(1, hueColor);
    ctx.fillStyle = gradH;
    ctx.fillRect(0, 0, w, h);

    // Vertical gradient: transparent → black
    const gradV = ctx.createLinearGradient(0, 0, 0, h);
    gradV.addColorStop(0, 'rgba(0,0,0,0)');
    gradV.addColorStop(1, 'rgba(0,0,0,1)');
    ctx.fillStyle = gradV;
    ctx.fillRect(0, 0, w, h);

    // Draw indicator circle
    const cx = customSat * w;
    const cy = (1 - customBri) * h;
    ctx.beginPath();
    ctx.arc(cx, cy, 7, 0, Math.PI * 2);
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, 7, 0, Math.PI * 2);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    ctx.stroke();
  }, [customHue, customSat, customBri]);

  // Draw hue slider canvas
  const drawHueCanvas = useCallback(() => {
    const canvas = hueCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    const grad = ctx.createLinearGradient(0, 0, w, 0);
    const stops = [0, 60, 120, 180, 240, 300, 360];
    stops.forEach((hue) => {
      grad.addColorStop(hue / 360, hsbToHex(hue === 360 ? 0 : hue, 1, 1));
    });
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Draw indicator
    const ix = (customHue / 360) * w;
    ctx.beginPath();
    ctx.roundRect(ix - 3, 0, 6, h, 2);
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.beginPath();
    ctx.roundRect(ix - 3, 0, 6, h, 2);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    ctx.stroke();
  }, [customHue]);

  // Redraw canvases when custom picker is visible
  useEffect(() => {
    if (!showCustom) return;
    drawSBCanvas();
    drawHueCanvas();
  }, [showCustom, drawSBCanvas, drawHueCanvas]);

  // SB canvas interaction
  const handleSBInteraction = useCallback((e, canvas) => {
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    setCustomSat(x);
    setCustomBri(1 - y);
  }, []);

  const handleSBDown = useCallback((e) => {
    e.preventDefault();
    sbDragging.current = true;
    handleSBInteraction(e, sbCanvasRef.current);
  }, [handleSBInteraction]);

  // Hue slider interaction
  const handleHueInteraction = useCallback((e, canvas) => {
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setCustomHue(x * 360);
  }, []);

  const handleHueDown = useCallback((e) => {
    e.preventDefault();
    hueDragging.current = true;
    handleHueInteraction(e, hueCanvasRef.current);
  }, [handleHueInteraction]);

  // Global mouse/touch move and up
  useEffect(() => {
    if (!showCustom) return;

    const handleMove = (e) => {
      if (sbDragging.current) {
        e.preventDefault();
        handleSBInteraction(e, sbCanvasRef.current);
      }
      if (hueDragging.current) {
        e.preventDefault();
        handleHueInteraction(e, hueCanvasRef.current);
      }
    };
    const handleUp = () => {
      sbDragging.current = false;
      hueDragging.current = false;
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };
  }, [showCustom, handleSBInteraction, handleHueInteraction]);

  // Handle hex input
  const handleHexInput = (val) => {
    const cleaned = val.startsWith('#') ? val : `#${val}`;
    setCustomHex(cleaned);
    if (/^#[0-9a-fA-F]{6}$/.test(cleaned)) {
      const hsb = hexToHsb(cleaned);
      setCustomHue(hsb.h);
      setCustomSat(hsb.s);
      setCustomBri(hsb.b);
    }
  };

  const handlePresetClick = (hex) => {
    onChange(hex);
    setOpen(false);
    setShowCustom(false);
  };

  const handleTransparentClick = () => {
    onChange('transparent');
    setOpen(false);
    setShowCustom(false);
  };

  const handleCustomAdd = () => {
    onChange(customHex);
    setOpen(false);
    setShowCustom(false);
  };

  const handleCustomCancel = () => {
    setShowCustom(false);
    // Reset to current value
    if (value && value !== 'transparent') {
      const hsb = hexToHsb(value);
      setCustomHue(hsb.h);
      setCustomSat(hsb.s);
      setCustomBri(hsb.b);
      setCustomHex(value);
    }
  };

  const openCustomPicker = () => {
    // Initialize custom picker from current value
    if (value && value !== 'transparent') {
      const hsb = hexToHsb(value);
      setCustomHue(hsb.h);
      setCustomSat(hsb.s);
      setCustomBri(hsb.b);
      setCustomHex(value);
    }
    setShowCustom(true);
  };

  const isTransparent = value === 'transparent';

  return (
    <div className="relative inline-flex" ref={containerRef}>
      {/* Trigger button — circular color swatch */}
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen(!open)}
        className={`w-6 h-6 rounded-full border-2 transition-colors flex items-center justify-center ${
          open ? 'border-gray-800' : 'border-gray-300 hover:border-gray-400'
        }`}
        style={isTransparent ? {} : { backgroundColor: value }}
        title={label}
      >
        {isTransparent && <TransparentSwatchSmall />}
      </button>

      {/* Popover */}
      {open && (
        <div
          ref={popoverRef}
          className={`absolute z-50 bg-white shadow-lg rounded-lg border border-gray-200 p-3 ${
            flipUp ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
          style={{ left: popoverLeft !== null ? `${popoverLeft}px` : 0, minWidth: showCustom ? 224 : 180 }}
        >
          {!showCustom ? (
            /* --- Preset Color Grid --- */
            <div className="grid grid-cols-4 gap-2 justify-items-center">
              {PRESET_COLORS.map((hex) => {
                const isActive = value === hex;
                const isWhite = hex === '#FFFFFF';
                return (
                  <button
                    key={hex}
                    onClick={() => handlePresetClick(hex)}
                    className={`w-9 h-9 rounded-full border-2 transition-all hover:scale-105 ${
                      isActive
                        ? 'border-brand-blue ring-2 ring-brand-blue/30'
                        : isWhite
                        ? 'border-gray-200 hover:border-gray-300'
                        : 'border-transparent hover:border-gray-200'
                    }`}
                    style={{ backgroundColor: hex }}
                    title={hex}
                  />
                );
              })}

              {/* None / transparent */}
              {allowTransparent && (
                <button
                  onClick={handleTransparentClick}
                  className={`w-9 h-9 rounded-full border-2 transition-all hover:scale-105 flex items-center justify-center ${
                    isTransparent
                      ? 'border-brand-blue ring-2 ring-brand-blue/30'
                      : 'border-transparent hover:border-gray-200'
                  }`}
                  title="None (transparent)"
                >
                  <TransparentIcon size={36} active={isTransparent} />
                </button>
              )}

              {/* Custom (+) button */}
              <button
                onClick={openCustomPicker}
                className="w-9 h-9 rounded-full border-2 border-dashed border-gray-300 transition-all hover:scale-105 hover:border-gray-400 flex items-center justify-center text-gray-400 hover:text-gray-500"
                title="Custom color"
              >
                <Plus size={16} />
              </button>
            </div>
          ) : (
            /* --- Custom Color Picker --- */
            <div className="flex flex-col gap-3">
              {/* Saturation/Brightness canvas */}
              <canvas
                ref={sbCanvasRef}
                width={200}
                height={150}
                className="w-[200px] h-[150px] rounded-md cursor-crosshair block"
                onMouseDown={handleSBDown}
                onTouchStart={handleSBDown}
              />

              {/* Hue slider */}
              <canvas
                ref={hueCanvasRef}
                width={200}
                height={12}
                className="w-[200px] h-3 rounded-full cursor-pointer block"
                onMouseDown={handleHueDown}
                onTouchStart={handleHueDown}
              />

              {/* Preview + Hex input */}
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-md border border-gray-200 shrink-0"
                  style={{ backgroundColor: customHex }}
                />
                <div className="flex items-center border border-gray-300 rounded-md overflow-hidden flex-1">
                  <span className="px-1.5 text-xs text-gray-400 bg-gray-50 h-7 flex items-center">#</span>
                  <input
                    type="text"
                    value={customHex.replace('#', '')}
                    onChange={(e) => handleHexInput(e.target.value)}
                    maxLength={6}
                    className="w-full h-7 px-1 text-xs font-mono focus:outline-none"
                    spellCheck={false}
                  />
                </div>
              </div>

              {/* Cancel + Add buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleCustomCancel}
                  className="flex-1 h-7 text-xs font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomAdd}
                  className="flex-1 h-7 text-xs font-medium rounded-md bg-brand-blue text-white hover:bg-brand-dark transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
